import { useState, useEffect, useCallback, useRef } from 'react';
import reviewService from '../services/reviewService';
import {
  BatteryReview,
  PaginationResponse,
  ReviewSummary,
} from '../types/api';

type ReviewSortField = 'createdAt' | 'rating' | 'helpful';

export interface ReviewQueryParams {
  page?: number;
  limit?: number;
  rating?: number;
  sortBy?: ReviewSortField;
  sortOrder?: 'asc' | 'desc';
}

interface UseBatteryReviewsResult {
  reviews: BatteryReview[];
  summary: ReviewSummary | null;
  pagination: PaginationResponse | null;
  loading: boolean;
  error: string | null;
  setPage: (page: number) => void;
  setRating: (rating?: number) => void;
  setSort: (sortBy: ReviewSortField, sortOrder?: 'asc' | 'desc') => void;
  reload: () => void;
}

const DEFAULT_PARAMS: Required<Pick<ReviewQueryParams, 'page' | 'limit' | 'sortBy' | 'sortOrder'>> =
  {
    page: 1,
    limit: 10,
    sortBy: 'createdAt',
    sortOrder: 'desc',
  };

const DEFAULT_PAGINATION: PaginationResponse = {
  page: 1,
  limit: DEFAULT_PARAMS.limit,
  total: 0,
  totalPages: 0,
};

const ensureDate = (value: Date | string): Date =>
  value instanceof Date ? new Date(value.getTime()) : new Date(value);

const normalizeReview = (review: BatteryReview): BatteryReview => ({
  ...review,
  createdAt: ensureDate(review.createdAt),
  updatedAt: ensureDate(review.updatedAt),
  images: Array.isArray(review.images) ? review.images : [],
});

export function useBatteryReviews(
  batteryId?: string,
  initialParams?: ReviewQueryParams
): UseBatteryReviewsResult {
  const [params, setParams] = useState<ReviewQueryParams>({
    ...DEFAULT_PARAMS,
    ...initialParams,
  });
  const [reviews, setReviews] = useState<BatteryReview[]>([]);
  const [summary, setSummary] = useState<ReviewSummary | null>(null);
  const [pagination, setPagination] =
    useState<PaginationResponse | null>(DEFAULT_PAGINATION);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [refreshToken, setRefreshToken] = useState(0);
  const previousParamsRef = useRef<ReviewQueryParams | null>(null);
  const previousBatteryIdRef = useRef<string | undefined>(undefined);
  const isLoadingRef = useRef(false);

  const fetchReviews = useCallback(async () => {
    if (!batteryId) {
      setReviews([]);
      setSummary(null);
      setPagination(DEFAULT_PAGINATION);
      setLoading(false);
      previousBatteryIdRef.current = undefined;
      return;
    }

    // Verificar se realmente mudou
    const batteryIdChanged = previousBatteryIdRef.current !== batteryId;
    const paramsChanged = !previousParamsRef.current || 
      JSON.stringify(previousParamsRef.current) !== JSON.stringify(params);

    // Se não mudou e já está carregando, não fazer nada
    if (!batteryIdChanged && !paramsChanged && isLoadingRef.current) {
      return;
    }

    // Se não mudou nada e já foi carregado, não fazer nada (evita chamadas duplicadas no React Strict Mode)
    if (!batteryIdChanged && !paramsChanged && !isLoadingRef.current && previousBatteryIdRef.current === batteryId) {
      return;
    }

    isLoadingRef.current = true;
    previousBatteryIdRef.current = batteryId;
    previousParamsRef.current = { ...params };

    setLoading(true);
    setError(null);

    try {
      const response = await reviewService.listByBattery(batteryId, params);
      setReviews(response.reviews.map(normalizeReview));
      setSummary(response.summary);
      setPagination(response.pagination);
    } catch (err) {
      const message =
        err && typeof err === 'object' && 'message' in err
          ? String((err as { message?: string }).message)
          : 'Erro ao carregar avaliações';

      setError(message);
      setReviews([]);
      setSummary(null);
      setPagination(DEFAULT_PAGINATION);
    } finally {
      setLoading(false);
      isLoadingRef.current = false;
    }
  }, [batteryId, params]);

  useEffect(() => {
    fetchReviews();
  }, [fetchReviews, refreshToken]);

  useEffect(() => {
    setParams((prev) => ({
      ...DEFAULT_PARAMS,
      ...prev,
      ...initialParams,
    }));
  }, [initialParams]);

  const setPage = useCallback((page: number) => {
    setParams((prev) => ({
      ...prev,
      page: page < 1 ? 1 : page,
    }));
  }, []);

  const setRating = useCallback((rating?: number) => {
    setParams((prev) => ({
      ...prev,
      rating,
      page: 1,
    }));
  }, []);

  const setSort = useCallback(
    (sortBy: ReviewSortField, sortOrder?: 'asc' | 'desc') => {
      setParams((prev) => ({
        ...prev,
        sortBy,
        sortOrder: sortOrder ?? prev.sortOrder ?? DEFAULT_PARAMS.sortOrder,
        page: 1,
      }));
    },
    []
  );

  const reload = useCallback(() => {
    setRefreshToken((prev) => prev + 1);
  }, []);

  return {
    reviews,
    summary,
    pagination,
    loading,
    error,
    setPage,
    setRating,
    setSort,
    reload,
  };
}


